/* Reglas de acceso por perfil y zona, las mismas que aplica el backend
   (wlc_back/Utils/Permisos.cs) y que tenía la plataforma antigua: el
   Administrador global lo ve todo y elige zona; el resto de perfiles queda en la
   zona de su usuario. El backend es quien manda; esto solo evita enseñar en
   pantalla lo que luego se va a denegar. */

export const PERFIL = {
    ADMINISTRADOR_GLOBAL: 1,
    ADMINISTRADOR_ZONA: 2,
    RESPONSABLE_OBRA: 3,
    INTERMEDIARIO: 4,
};

export const esAdminGlobal = (role) =>
    Number(role) === PERFIL.ADMINISTRADOR_GLOBAL;

export const esIntermediario = (role) =>
    Number(role) === PERFIL.INTERMEDIARIO;

/* Administración (países, comunidades, provincias, productores e
   intermediarios): todos menos el Intermediario, como en el legacy. */
export const puedeVerAdministracion = (role) =>
    role != null && !esIntermediario(role);

/* Informe de visitas a gestores: solo administradores (perfiles 1 y 2). El
   backend se lo deniega igualmente al resto. */
export const puedeVerInformeVisitas = (role) =>
    Number(role) === PERFIL.ADMINISTRADOR_GLOBAL ||
    Number(role) === PERFIL.ADMINISTRADOR_ZONA;

/* Obras menores, como en la antigua (_Layout.cshtml): solo en Europa (zona 1).
   El Administrador global ve todas las zonas y ya no tiene selector de zona,
   así que la ve siempre. */
export const puedeVerObrasMenores = (user) =>
    !!user && (esAdminGlobal(user.role) || zonaFijaUsuario(user) === "1");

/* Perfil leído del token, para los componentes que no reciben el usuario por
   props (buscador de la cabecera, barra de filtros). */
export const rolDelToken = () => {
    try {
        const token = localStorage.getItem("token");
        return JSON.parse(atob(token.split(".")[1]))[
            "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
        ];
    } catch {
        return null;
    }
};

/* Perfil y zona del token, con la misma forma que el usuario de App.jsx. */
export const usuarioDelToken = () => {
    try {
        const token = localStorage.getItem("token");
        const datos = JSON.parse(atob(token.split(".")[1]));
        return {
            role: datos[
                "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
            ],
            codZona: datos["CodZona"] || "",
        };
    } catch {
        return null;
    }
};

export const esCampoZona = (key) => key === "codZona" || key === "zona";

/* El filtro o la columna de Zona sobran cuando el usuario no es Administrador
   global y todo lo que ve es de una sola zona (el backend ya le recorta a la
   suya). Si los datos traen varias zonas (catálogos de Administración), se
   mantiene. Sin perfil conocido no se oculta nada. */
export const ocultarZona = (role, rows) => {
    if (role == null || role === "" || esAdminGlobal(role)) return false;
    const zonas = new Set(
        (rows ?? [])
            .map((row) => row?.codZona ?? row?.CodZona)
            .filter((zona) => zona !== undefined && zona !== null && zona !== "")
            .map(String)
    );
    return zonas.size <= 1;
};

/* Acciones de fila de las tablas (Table.tsx): "Editar", "Eliminar" o un objeto
   con actionType "validate" o danger. El intermediario solo consulta, salvo en
   las tablas donde la antigua le dejaba trabajar (intermediarioPuedeEscribir:
   residuos y documentos de su obra, gestores, obras menores). Validar es siempre
   de administradores. */
const esValidacion = (action) =>
    typeof action === "object" && action?.actionType === "validate";

const esAccionDeEscritura = (action) =>
    action === "Editar" ||
    action === "Eliminar" ||
    esValidacion(action) ||
    (typeof action === "object" && action?.danger === true);

export const accionPermitida = (role, action, intermediarioPuedeEscribir) => {
    if (!esIntermediario(role)) return true;
    return intermediarioPuedeEscribir
        ? !esValidacion(action)
        : !esAccionDeEscritura(action);
};

export const puedeAnadirEnTabla = (role, intermediarioPuedeEscribir) =>
    !esIntermediario(role) || !!intermediarioPuedeEscribir;

/* Zona a la que queda fijado el usuario, como string ("1", "2"...), o null si
   puede elegir (Administrador global). Sin zona cae en Europa, como en
   Session.getZona del legacy. */
export const zonaFijaUsuario = (user) => {
    if (!user || esAdminGlobal(user.role)) return null;
    const zona = String(user.codZona ?? "").trim();
    return zona !== "" ? zona : "1";
};
